import { Page, View, Document, Text } from "@react-pdf/renderer";
import {
  FormHeading,
  PageContent,
  Flex,
  FlexRow,
  H2,
  H3,
  H4,
  P,
  CheckInput,
  TextInput,
  PageHeading,
  TextAreaInput,
  ListItem,
} from "./components";
import {
  addWeeksToDate,
  calculateAge,
  formatDateToDD_MMM_YYYY,
  formatPhone,
} from "../../helpers/functions";
import {
  biologicalSexOptions,
  countryOptions,
  designationOptions,
  genderOptions,
  insurancePlanOptions,
  relationshipTypeOptions,
  relationshipTypeThirdStepOptions,
  stateOptions,
} from "../patient/create/options";
import { trimesterEnum } from "../patient/clinical-services/service-steps/pre-assessment/data";

export const InitialAssessmentForm = ({ patient, initialAssessmentForm }) => {
  if (!patient || !initialAssessmentForm) {
    alert("no patient or initialAssessmentForm");
    return null;
  }

  const serviceName = "ALLERGIC RHINITIS - TREATMENT";

  const caregiver = patient.caregiver?.[0] || null;
  const careProvider = patient.careProvider?.[0] || null;
  const altCareProvider = patient.careProvider?.[1] || null;

  const formValues = {
    serviceDate: initialAssessmentForm.preAssessment.serviceDate
      ? formatDateToDD_MMM_YYYY(initialAssessmentForm.preAssessment.serviceDate)
      : "",
    patientHasHistory:
      !!initialAssessmentForm.preAssessment?.previousHistoryIndicator,
    eligibilityInformation: {
      maxAllowed: "", // TODO
      claimNumber: "", // TODO
    },
    patientInformation: {
      patientName: (
        (patient.firstName || "") +
        (patient.middleName ? ` ${patient.middleName}` : "") +
        (patient.lastName ? ` ${patient.lastName}` : "")
      ).trim(),
      dateOfBirth: patient.dateOfBirth
        ? formatDateToDD_MMM_YYYY(patient.dateOfBirth)
        : "",
      age: patient.dateOfBirth ? calculateAge(patient.dateOfBirth) : "",
      gender: genderOptions.find((g) => g.value === patient.gender)?.label,
      biologicalSex: biologicalSexOptions.find(
        (g) => g.value === patient.biologicalSex
      )?.label,
      insuranceInfo: {
        healthCardNumber: patient.provincialInsurance?.cardNumber,
        versionCode: patient.provincialInsurance?.versionCode,
        expiryDate: patient.provincialInsurance?.expiryDate,
        noProvincialInsurance: !patient.provincialInsurance,
        referralSuggested: false, // TODO: we'll do that later, as we are not storing that in the BE - Dhruv
        thirdPartyInsurance: patient.thirdPartyInsurance
          ?.map?.(
            (i) =>
              insurancePlanOptions.find((p) => p.value === i.insurancePlanName)
                ?.label || "None"
          )
          ?.join?.(", "),
      },
    },
    patientContactInformation: {
      noPhoneInformation: !patient.contactInfo,
      primaryPhone: patient.contactInfo?.primaryPhone
        ? `+${patient.contactInfo.primaryPhoneCountryCode} ${formatPhone(
            patient.contactInfo.primaryPhone
          )}`
        : "",
      secondaryPhone: patient.contactInfo?.secondaryPhone
        ? `+${patient.contactInfo.secondaryPhoneCountryCode} ${formatPhone(
            patient.contactInfo.secondaryPhone
          )}`
        : "",
      email: patient.contactInfo?.email,
      noPermanentAddress: !patient.contactInfo?.address,
      livingInLongTermCare: patient.contactInfo?.longTermCare,
      longTermFacilityName: patient.contactInfo?.nameOfFacility || "",
      address: patient.contactInfo?.address,
      unitNumber: patient.contactInfo?.unitNumber,
      poBox: patient.contactInfo?.poBox,
      country: countryOptions.find(
        (c) => c.value === patient.contactInfo?.country
      )?.label,
      province: stateOptions.find(
        (s) => s.value === patient.contactInfo?.province
      )?.label,
      city: patient.contactInfo?.city,
      postalCode: patient.contactInfo?.postalCode,
    },
    alternateCaregiverAgentInformation: {
      noAlternateCaregiver: !caregiver,
      alternateCaregiverAgentName: caregiver
        ? (
            (caregiver.firstName || "") +
            (caregiver.middleName ? ` ${caregiver.middleName}` : "") +
            (caregiver.lastName ? ` ${caregiver.lastName}` : "")
          ).trim()
        : "",
      relationship: caregiver
        ? [
            ...relationshipTypeOptions,
            ...relationshipTypeThirdStepOptions,
          ].find((r) => r.value === caregiver.relationship)?.label
        : "",
      primaryPhone: caregiver
        ? caregiver.contactInfo?.primaryPhone
          ? `+${caregiver.contactInfo.primaryPhoneCountryCode} ${formatPhone(
              caregiver.contactInfo.primaryPhone
            )}`
          : ""
        : "",
      secondaryPhone: caregiver
        ? caregiver.contactInfo?.secondaryPhone
          ? `+${caregiver.contactInfo.secondaryPhoneCountryCode} ${formatPhone(
              caregiver.contactInfo.secondaryPhone
            )}`
          : ""
        : "",
      email: caregiver?.contactInfo?.email,
    },
    primaryPhysicianOrAltHealthcareProvider: {
      noProvider: !careProvider,
      providerName: careProvider
        ? (
            (careProvider.firstName || "") +
            (careProvider.middleName ? ` ${careProvider.middleName}` : "") +
            (careProvider.lastName ? ` ${careProvider.lastName}` : "")
          ).trim()
        : "",
      licenseNumber: careProvider?.licenseNumber,
      designation: careProvider
        ? designationOptions.find((d) => d.value === careProvider.title)?.label
        : "",
      phone: careProvider
        ? careProvider.contactInfo?.primaryPhone
          ? `+${careProvider.contactInfo.primaryPhoneCountryCode} ${formatPhone(
              careProvider.contactInfo.primaryPhone
            )}`
          : ""
        : "",
      fax: careProvider?.contactInfo?.fax,
      email: careProvider?.contactInfo?.email,
    },
    clinicalServiceInformation: {
      appointmentType: {
        scheduled:
          initialAssessmentForm.preAssessment?.appointmentType === "Scheduled",
        walkIn:
          initialAssessmentForm.preAssessment?.appointmentType === "Walk-In",
      },
      visitType: {
        inPerson:
          initialAssessmentForm.preAssessment?.visitType === "In-Person",
        virtual: initialAssessmentForm.preAssessment?.visitType === "Virtual",
      },
      attendees: {
        // from Dhruv: preAssessment.attendees and you will see whether it is patient or alternate caregiver
        // however they are only IDs, how to get info from them?
        patient: false, // TODO
        patientAndCaregiver: false, // TODO
      },
      patientPresentingSymptoms:
        initialAssessmentForm.preAssessment?.presentingSymptoms,
    },
    consentInformation: {
      informedConsentObtainedBy: {
        patient:
          initialAssessmentForm.preAssessment?.patientAcknowledgeIndicator,
        patientAndCaregiver: false, // TODO: preAssessment.attendees and you will see whether it is patient or alternate caregiver, and then patientAcknowledgeIndicator should be true - Dhruv
        pharmacist:
          initialAssessmentForm.preAssessment?.pharmacistAcknowledgeIndicator,
      },
    },
    preAssessmentSpecialPopulations: {
      pregnancy: {
        checked:
          initialAssessmentForm.preAssessment?.pregnancyIndicator === "Yes",
        trimester: trimesterEnum.options.find(
          (t) =>
            t.value === initialAssessmentForm.preAssessment?.pregnancyTrimester
        )?.label,
        expectancyDate: initialAssessmentForm.preAssessment?.expectancyDate
          ? formatDateToDD_MMM_YYYY(
              initialAssessmentForm.preAssessment?.expectancyDate
            )
          : "",
      },
      breastfeeding: {
        checked:
          initialAssessmentForm.preAssessment?.breastFeedingIndicator === "Yes",
        ageOfInfant: initialAssessmentForm.preAssessment?.expectancyDate
          ? calculateAge(initialAssessmentForm.preAssessment?.expectancyDate)
          : "",
      },
      residingInLongTermCare: !!patient.contactInfo?.longTermCare,
      comments: patient.contactInfo?.comments,
    },
    allergyInformation: {
      noKnownAllergies: !patient.allergyInfo?.allergies?.length,
      hasKnownAllergies: patient.allergyInfo?.allergies?.length > 0,
      allergies:
        patient.allergyInfo?.allergies
          ?.map?.((a) => a.allergyName)
          ?.join(", ") || "",
    },
    lifestyleFactors: {
      alcohol: initialAssessmentForm.clinicalAssessment?.alcohol || "",
      caffeine: initialAssessmentForm.clinicalAssessment?.caffeine || "",
      smoking: initialAssessmentForm.clinicalAssessment?.smoking || "",
      smokingCessationConsent:
        !!initialAssessmentForm.clinicalAssessment?.smokingCessationIndicator,
      recreationalDrugs:
        initialAssessmentForm.clinicalAssessment?.recreationalDrugs?.join?.(
          ", "
        ) ||
        initialAssessmentForm.clinicalAssessment?.recreationalDrugs ||
        "",
      exercise: initialAssessmentForm.clinicalAssessment?.exercise
        ? initialAssessmentForm.clinicalAssessment.exercise +
          (initialAssessmentForm.clinicalAssessment?.exerciseTypes?.length
            ? " - " +
              initialAssessmentForm.clinicalAssessment.exerciseTypes.join(", ")
            : "")
        : "",
      culturalFactors:
        initialAssessmentForm.clinicalAssessment?.culturalFactors?.join?.(
          ", "
        ) ||
        initialAssessmentForm.clinicalAssessment?.culturalFactors ||
        "",
      lifestyleComments: initialAssessmentForm.clinicalAssessment?.lifestyle,
    },
    reasonsForReferral: {
      primaryPhysicianOrAltHealthcareProvider: false, // TODO: Let's connect about this, hard to type it out - Dhruv
      presentingSymptomsAndMinorAilment: {
        noSymptomsMatchMinorAilments: false, // TODO: Let's connect about this, hard to type it out - Dhruv
        noTypicalSignsAndSymptoms: false, // TODO: Let's connect about this, hard to type it out - Dhruv
      },
      specialPopulation: {
        pregnant:
          initialAssessmentForm.preAssessment?.pregnancyIndicator === "Yes",
        breastfeeding:
          initialAssessmentForm.preAssessment?.breastFeedingIndicator === "Yes",
      },
      redFlagSignsAndSymptoms: {
        redFlagSymptoms:
          !!initialAssessmentForm.signsAndSymptoms?.redFlagSymptoms?.length,
        redFlagSymptomsValue:
          initialAssessmentForm.signsAndSymptoms?.redFlagSymptoms?.join?.(", "),
        infectiveRhinitis: false, // TODO: Let's connect about this, hard to type it out - Dhruv
        chronicRhinosinusitis:
          !!initialAssessmentForm.signsAndSymptoms
            ?.chronicRhinosinusitisSymptoms?.length,
      },
      redFlagMedicalConditions: {
        // Dhruv: Let's connect about these, hard to type it out
        otitisMedia: false, // TODO
        chronicRecurrentSinusitis: false, // TODO
        uncontrolledThyroid: false, // TODO
        uncontrolledAsthma: false, // TODO
      },
      drugInducedAllergicRhinitis: {
        prescriptionMedications:
          !!initialAssessmentForm.clinicalAssessment?.anyPrescriptionMedication,
        prescriptionMedicationsValue:
          initialAssessmentForm.clinicalAssessment?.otherPrescriptionMedications?.join?.(
            ", "
          ) || "",
        oralContraceptives:
          !!initialAssessmentForm.clinicalAssessment?.oralContraceptives,
        nonPrescriptionMedications:
          !!initialAssessmentForm.clinicalAssessment?.nonPrescriptionMedications
            ?.length,
        // Dhruv: Let's connect about this, hard to type it out
        herbalVitamins: false, // TODO
        sampleMedications: false, // TODO
        cocaine: false, // TODO
      },
      differentialAssessment: {
        irritantRhinitis:
          !!initialAssessmentForm.differentialAssessment?.irritantRhinitis
            ?.length,
        irritantRhinitisValue:
          initialAssessmentForm.differentialAssessment?.irritantRhinitis?.join?.(
            ", "
          ) || "",
        occupationalRhinitis:
          !!initialAssessmentForm.differentialAssessment
            ?.occupationalRhinitisIndicator,
        structuralOrMechanical:
          !!initialAssessmentForm.differentialAssessment
            ?.structuralOrMechanicalConditions?.length,
        systemicConditions:
          !!initialAssessmentForm.differentialAssessment?.systemicConditions
            ?.length,
      },
      interventions: {
        suggestedToDiscontinueMedication: false, // TODO: unchecked for now - Dhruv
        suggestedToChangeMedication: false, // TODO: unchecked for now - Dhruv
      },
    },
    typicalSignsAndSymptoms: {
      nasalSymptoms: {
        checked:
          !!initialAssessmentForm.signsAndSymptoms?.nasalSymptoms?.length,
        rhinorrhea:
          !!initialAssessmentForm.signsAndSymptoms?.nasalSymptoms?.includes?.(
            "runnyNose"
          ),
        itchyNose:
          !!initialAssessmentForm.signsAndSymptoms?.nasalSymptoms?.includes?.(
            "itchyNoseEyesThroat"
          ),
        sneezing:
          !!initialAssessmentForm.signsAndSymptoms?.nasalSymptoms?.includes?.(
            "sneezing"
          ),
      },
      otherARSymptoms:
        !!initialAssessmentForm.signsAndSymptoms?.otherSymptoms?.length,
      nasalCongestion:
        !!initialAssessmentForm.signsAndSymptoms?.nasalCongestion?.length,
      eyeSymptoms:
        !!initialAssessmentForm.signsAndSymptoms?.otherSymptoms?.includes?.(
          "wateryEyes"
        ),
      clinical: {
        allergicSalute:
          !!initialAssessmentForm.signsAndSymptoms?.visibleSymptoms?.includes?.(
            "allergicSalute"
          ),
        allergicShiners:
          !!initialAssessmentForm.signsAndSymptoms?.visibleSymptoms?.includes?.(
            "allergicShiner"
          ),
        noTypicalSignsAndSymptoms:
          !initialAssessmentForm.signsAndSymptoms?.visibleSymptoms?.length,
      },
    },
    classificationOfMinorAilment: {
      symptomDuration: {
        mildIntermittent:
          initialAssessmentForm.treatment?.frequencyOfSymptoms ===
            "lessThanFourWeeks" &&
          initialAssessmentForm.treatment?.levelOfSeverity ===
            "symptomsDoNotInterfereWithActivities",
        mildPersistent:
          initialAssessmentForm.treatment?.frequencyOfSymptoms ===
            "moreThanFourWeeks" &&
          initialAssessmentForm.treatment?.levelOfSeverity ===
            "symptomsDoNotInterfereWithActivities",
        moderateSevereIntermittent:
          initialAssessmentForm.treatment?.frequencyOfSymptoms ===
            "lessThanFourWeeks" &&
          initialAssessmentForm.treatment?.levelOfSeverity ===
            "symptomsInterfereWithActivities",
        moderateSeverePersistent:
          initialAssessmentForm.treatment?.frequencyOfSymptoms ===
            "moreThanFourWeeks" &&
          initialAssessmentForm.treatment?.levelOfSeverity ===
            "symptomsInterfereWithActivities",
      },
      symptomsCorrelateAllergenExposure:
        !!initialAssessmentForm.signsAndSymptoms?.allergens?.length,
    },
    treatmentHistory: {
      // TODO wait for prescription data
      noPastMedications: false,
      allergenAvoidance: {
        checked: false,
        comments: "",
        howLong: "",
        effective: false,
      },
      nasalIrrigationLubricant: {
        checked: false,
        comments: "",
        howLong: "",
        effective: false,
        sideEffects: false,
        sideEffectsComments: "",
      },
      oralNasalDecongestant: {
        checked: false,
        comments: "",
        howLong: "",
        effective: false,
        sideEffects: false,
        sideEffectsComments: "",
      },
      oralAntihistamine: {
        checked: false,
        comments: "",
        howLong: "",
        effective: false,
        sideEffects: false,
        sideEffectsComments: "",
      },
      intranasalCorticosteroid: {
        checked: false,
        comments: "",
        howLong: "",
        effective: false,
        sideEffects: false,
        sideEffectsComments: "",
      },
      intranasalAntihistamineCorticosteroid: {
        checked: false,
        comments: "",
        howLong: "",
        effective: false,
        sideEffects: false,
        sideEffectsComments: "",
      },
    },
    treatment: {
      prescriptionsIssued:
        initialAssessmentForm.treatment?.nasalSalineRinsePrescriptions
          ?.length ||
        initialAssessmentForm.treatment?.nasalSalineSprayPrescriptions
          ?.length ||
        initialAssessmentForm.treatment?.nasalLubricantPrescriptions?.length ||
        initialAssessmentForm.treatment?.firstLineTherapyPrescriptions
          ?.length ||
        initialAssessmentForm.treatment?.stepUpTherapyPrescriptions?.length ||
        initialAssessmentForm.treatment
          ?.alternativeFirstLineTherapyPrescriptions?.length ||
        initialAssessmentForm.treatment?.addOnTherapyPrescriptions?.length,
      nonPharmacologicalTreatment: {
        allergenAvoidance: true,
        nasalRinses:
          initialAssessmentForm.treatment?.nasalIrrigations?.includes?.(
            "nasalRinses"
          ) ||
          initialAssessmentForm.treatment?.nasalIrrigations?.includes?.(
            "nasalSprays"
          ),
        nasalLubricants:
          initialAssessmentForm.treatment?.nasalIrrigations?.includes?.(
            "nasalLubricants"
          ),
      },
      pharmacologicalTreatment: {
        // TODO: difference b/w mildIntermittent, mildPersistent, and moderateToSevere ?
        // I will need to connect with you when the logic is ready, I will keep these in my mind - Dhruv
        mildIntermittent: {
          oralAntihistamine:
            !!initialAssessmentForm.treatment?.oralAntihistamines,
          oralAntihistamineName: "Exact Medication Name", // TODO
          intranasalCorticosteroid:
            !!initialAssessmentForm.treatment?.intranasalCorticosteroids,
          intranasalCorticosteroidAntihistamine:
            !!initialAssessmentForm.treatment
              ?.intranasalAntihistaminesAndCorticosteroids,
        },
        mildPersistent: {
          intranasalCorticosteroid:
            !!initialAssessmentForm.treatment?.intranasalCorticosteroids,
          intranasalCorticosteroidAntihistamine:
            !!initialAssessmentForm.treatment
              ?.intranasalAntihistaminesAndCorticosteroids,
        },
        moderateToSevere: {
          intranasalCorticosteroid:
            !!initialAssessmentForm.treatment?.intranasalCorticosteroids,
          intranasalCorticosteroidAntihistamine:
            !!initialAssessmentForm.treatment
              ?.intranasalAntihistaminesAndCorticosteroids,
        },
      },
      addOnTherapy: {
        oralDecongestant:
          !!initialAssessmentForm.treatment?.nasalOrOralDecongestants?.includes?.(
            "oralDecongestants"
          ),
        oralAntihistamines:
          !!initialAssessmentForm.treatment?.oralAntihistamines,
      },
    },
    otcConsultInformation: {
      otcConsultProvided: false, // TODO: gray it out?
      otcRecommendationsIssued: false, // TODO: gray it out?
      nonPharmacologicalTreatment: {
        allergenAvoidanceTips: true,
        nasalRinses: false, // TODO: if you are prescribed, any of those items i.e nasalSalineSprayPrescriptions inside treatment object in swagger
        nasalLubricants: false, // TODO: same thing as above but different object, check swagger
      },
    },
    educationalInformation: {
      educationProvided: false, // TODO: checked when treatment object has at least one prescription i.e nasalSalineSprayPrescriptions
      storyboardProvided: false, // TODO: checked when treatment object has at least one prescription i.e nasalSalineSprayPrescriptions
    },
    notificationType: {
      primaryCarePhysician: {
        checked: !!careProvider,
        name: careProvider
          ? (
              (careProvider.firstName || "") +
              (careProvider.middleName ? ` ${careProvider.middleName}` : "") +
              (careProvider.lastName ? ` ${careProvider.lastName}` : "")
            ).trim()
          : "",
      },
      altHealthCareProvider: {
        checked: !!altCareProvider,
        designation: altCareProvider?.title || "",
        name: altCareProvider
          ? (
              (altCareProvider.firstName || "") +
              (altCareProvider.middleName
                ? ` ${altCareProvider.middleName}`
                : "") +
              (altCareProvider.lastName ? ` ${altCareProvider.lastName}` : "")
            ).trim()
          : "",
      },
      date: initialAssessmentForm.preAssessment.serviceDate
        ? formatDateToDD_MMM_YYYY(
            initialAssessmentForm.preAssessment.serviceDate
          )
        : "",
      method: {
        email: false,
        fax: true,
        phone: false,
        inPerson: false,
      },
    },
    followUpInformation: {
      date:
        initialAssessmentForm.preAssessment.serviceDate &&
        serviceName === "ALLERGIC RHINITIS - TREATMENT"
          ? formatDateToDD_MMM_YYYY(
              addWeeksToDate(initialAssessmentForm.preAssessment.serviceDate, 2)
            )
          : "yyyy-mon-dd",
    },
    prescribingPharmacistInformation: {
      // Dhruv: we would not have this right now, ask Vesna if we can put placeholder info
      name: "", // TODO
      licenseNumber: "", // TODO
      signature: "", // TODO
    },
    pharmacyInformation: {
      // Dhruv: we would not have this right now, ask Vesna if we can put placeholder info
      name: "", // TODO
      storeNumber: "", // TODO
      phone: "", // TODO
      fax: "", // TODO
      signature: "", // TODO
      address: "", // TODO
    },
  };

  return (
    <Document>
      <Page
        size="A4"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 30,
        }}
      >
        <FormHeading
          title="Pharmacist Initial Assessment Form"
          pharmacyName="Pharmacy Name/Logo"
        />
        <PageContent>
          <Flex gap={16}>
            <H4>Service Date: {formValues.serviceDate}</H4>
            <H2>Minor Ailment Service: {serviceName}</H2>
            <Flex gap={6}>
              <CheckInput
                checked={formValues.patientHasHistory}
                label="Patient has a history or recent diagnosis of selected Minor Ailment"
              />
              <CheckInput
                checked={!formValues.patientHasHistory}
                label="Patient DOES NOT have a history or recent diagnosis of selected Minor Ailment"
              />
            </Flex>
          </Flex>
          <Flex gap={8}>
            <H2 filled>Eligibility Information</H2>
            <FlexRow gap={12}>
              <TextInput
                label="Max Allowed"
                value={formValues.eligibilityInformation.maxAllowed}
              />
              <TextInput
                label="Claim Number"
                value={formValues.eligibilityInformation.claimNumber}
              />
            </FlexRow>
          </Flex>
          <Flex gap={16}>
            <H2 filled>Patient Information</H2>
            <Flex gap={8}>
              <TextInput
                label="Patient Name"
                value={formValues.patientInformation.patientName}
                required
              />
              <FlexRow gap={12}>
                <TextInput
                  label="Date Of Birth"
                  value={formValues.patientInformation.dateOfBirth}
                  required
                />
                <TextInput
                  label="Age"
                  value={formValues.patientInformation.age}
                  required
                />
              </FlexRow>
              <FlexRow gap={12}>
                <TextInput
                  label="Gender (Optional)"
                  value={formValues.patientInformation.gender}
                />
                <TextInput
                  label="Biological Sex (Optional)"
                  value={formValues.patientInformation.biologicalSex}
                />
              </FlexRow>
              <H3>Insurance Information</H3>
              <FlexRow gap={12}>
                <TextInput
                  label="Health Card Number"
                  value={
                    formValues.patientInformation.insuranceInfo.healthCardNumber
                  }
                />
                <FlexRow gap={12}>
                  <TextInput
                    label="Version Code"
                    value={
                      formValues.patientInformation.insuranceInfo.versionCode
                    }
                  />
                  <TextInput
                    label="Expiry Date"
                    value={
                      formValues.patientInformation.insuranceInfo.expiryDate
                    }
                  />
                </FlexRow>
              </FlexRow>
            </Flex>
          </Flex>
          <Flex gap={8}>
            <CheckInput
              checked={
                formValues.patientInformation.insuranceInfo
                  .noProvincialInsurance
              }
              label="No Provincial Insurance: Patient consents to pay for Clinical Service"
            />
            <FlexRow gap={8}>
              <CheckInput
                checked={
                  formValues.patientInformation.insuranceInfo.referralSuggested
                }
                label="REFERRAL Suggested:"
              />
              <View style={{ maxWidth: 308 }}>
                <P>
                  Patient does not consent to pay for Clinical Service {"\n"}
                  Cancel Service and Referral to Primary Provider or{"\n"}
                  Alternate Healthcare Provider
                </P>
              </View>
            </FlexRow>
            <TextInput
              label="Third Party Insurance Plan(s)- Optional"
              value={
                formValues.patientInformation.insuranceInfo.thirdPartyInsurance
              }
            />
          </Flex>
        </PageContent>
      </Page>

      <Page
        size="A4"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 30,
        }}
      >
        <PageHeading pharmacyName="Pharmacy Name/Logo" />
        <PageContent>
          <Flex gap={16}>
            <H2 filled>Patient Contact Information</H2>
            <Flex gap={8}>
              <Flex gap={4}>
                <CheckInput
                  checked={
                    formValues.patientContactInformation.noPhoneInformation
                  }
                  label="No Phone Information"
                />
                <FlexRow gap={12}>
                  <TextInput
                    label="Primary Phone Number"
                    required
                    value={formValues.patientContactInformation.primaryPhone}
                  />
                  <TextInput
                    label="Secondary Phone"
                    value={formValues.patientContactInformation.secondaryPhone}
                  />
                </FlexRow>
              </Flex>
              <TextInput
                label="Email Address"
                value={formValues.patientContactInformation.email}
              />
              <CheckInput
                checked={
                  formValues.patientContactInformation.noPermanentAddress
                }
                label="No Permanent Address"
              />
              <Flex gap={4}>
                <CheckInput
                  checked={
                    formValues.patientContactInformation.livingInLongTermCare
                  }
                  label="Living in Long Term Care"
                />
                <View style={{ paddingLeft: 22 }}>
                  <TextInput
                    label="Name of Facility (Optional)"
                    value={
                      formValues.patientContactInformation.longTermFacilityName
                    }
                  />
                </View>
              </Flex>
              <TextInput
                label="Address (Street Number and Name)"
                required
                value={formValues.patientContactInformation.address}
              />
              <FlexRow gap={12}>
                <TextInput
                  label="Unit Number"
                  value={formValues.patientContactInformation.unitNumber}
                />
                <TextInput
                  label="PO Box"
                  value={formValues.patientContactInformation.poBox}
                />
              </FlexRow>
              <FlexRow gap={12}>
                <TextInput
                  label="Country"
                  required
                  value={formValues.patientContactInformation.country}
                />
                <TextInput
                  label="Province"
                  required
                  value={formValues.patientContactInformation.province}
                />
              </FlexRow>
              <FlexRow gap={12}>
                <TextInput
                  label="City/Town"
                  required
                  value={formValues.patientContactInformation.city}
                />
                <TextInput
                  label="Postal Code"
                  required
                  value={formValues.patientContactInformation.postalCode}
                />
              </FlexRow>
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H2 filled>Alternate Caregiver / Agent Information (Optional)</H2>
            <Flex gap={8}>
              <CheckInput
                checked={
                  !formValues.alternateCaregiverAgentInformation
                    .noAlternateCaregiver
                }
                label="No Alternate Caregiver"
              />
              <TextInput
                label="Alternate Caregiver / Agent Name"
                required
                value={
                  formValues.alternateCaregiverAgentInformation
                    .alternateCaregiverAgentName
                }
              />
              <FlexRow gap={6}>
                <TextInput
                  label="Relationship"
                  required
                  value={
                    formValues.alternateCaregiverAgentInformation.relationship
                  }
                />
                <View style={{ minWidth: "50%" }}></View>
              </FlexRow>
              <FlexRow gap={12}>
                <TextInput
                  label="Primary Phone Number"
                  required
                  value={
                    formValues.alternateCaregiverAgentInformation.primaryPhone
                  }
                />
                <TextInput
                  label="Secondary Phone"
                  value={
                    formValues.alternateCaregiverAgentInformation.secondaryPhone
                  }
                />
              </FlexRow>
              <TextInput
                label="Email Address"
                value={formValues.alternateCaregiverAgentInformation.email}
              />
            </Flex>
          </Flex>
        </PageContent>
      </Page>

      <Page
        size="A4"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 30,
        }}
      >
        <PageHeading pharmacyName="Pharmacy Name/Logo" />
        <PageContent>
          <Flex gap={16}>
            <H2 filled>
              Primary Provider or Alternate Healthcare Provider Information
            </H2>
            <Flex gap={8}>
              <CheckInput
                checked={
                  formValues.primaryPhysicianOrAltHealthcareProvider.noProvider
                }
                label="No Primary Provider or Alternate Healthcare Provider"
              />
              <TextInput
                label="Primary Provider or Alternate Healthcare Provider Name"
                required
                value={
                  formValues.primaryPhysicianOrAltHealthcareProvider
                    .providerName
                }
              />
              <FlexRow gap={12}>
                <TextInput
                  label="License Number"
                  value={
                    formValues.primaryPhysicianOrAltHealthcareProvider
                      .licenseNumber
                  }
                />
                <TextInput
                  label="Designation"
                  value={
                    formValues.primaryPhysicianOrAltHealthcareProvider
                      .designation
                  }
                />
              </FlexRow>
              <FlexRow gap={12}>
                <TextInput
                  label="Phone"
                  value={
                    formValues.primaryPhysicianOrAltHealthcareProvider.phone
                  }
                />
                <TextInput
                  label="Fax"
                  value={formValues.primaryPhysicianOrAltHealthcareProvider.fax}
                />
              </FlexRow>
              <TextInput
                label="Email Address"
                value={formValues.primaryPhysicianOrAltHealthcareProvider.email}
              />
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H2 filled>Clinical Service Information</H2>
            <Flex gap={12}>
              <Flex gap={8}>
                <H3>Appointment Type</H3>
                <FlexRow gap={10}>
                  <View style={{ width: 152 }}>
                    <CheckInput
                      checked={
                        formValues.clinicalServiceInformation.appointmentType
                          .scheduled
                      }
                      label="Scheduled"
                    />
                  </View>
                  <CheckInput
                    checked={
                      formValues.clinicalServiceInformation.appointmentType
                        .walkIn
                    }
                    label="Walk-in"
                  />
                </FlexRow>
              </Flex>
              <Flex gap={8}>
                <H3>Visit Type</H3>
                <FlexRow gap={10}>
                  <View style={{ width: 152 }}>
                    <CheckInput
                      checked={
                        formValues.clinicalServiceInformation.visitType.inPerson
                      }
                      label="In-person"
                    />
                  </View>
                  <CheckInput
                    checked={
                      formValues.clinicalServiceInformation.visitType.virtual
                    }
                    label="Virtual"
                  />
                </FlexRow>
              </Flex>
              <Flex gap={8}>
                <H3>Attendee(s)</H3>
                <FlexRow gap={10}>
                  <View style={{ width: 152 }}>
                    <CheckInput
                      checked={
                        formValues.clinicalServiceInformation.attendees.patient
                      }
                      label="Patient"
                    />
                  </View>
                  <CheckInput
                    checked={
                      formValues.clinicalServiceInformation.attendees
                        .patientAndCaregiver
                    }
                    label="Patient & Alternate Caregiver"
                  />
                </FlexRow>
              </Flex>
              <Flex gap={8}>
                <H3>Patient Presenting Symptoms</H3>
                <TextAreaInput
                  lines={2}
                  value={
                    formValues.clinicalServiceInformation
                      .patientPresentingSymptoms
                  }
                />
              </Flex>
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H2 filled>Consent Information</H2>
            <Flex gap={8}>
              <H3>Informed Consent Obtained by:</H3>
              <CheckInput
                checked={
                  formValues.consentInformation.informedConsentObtainedBy
                    .patient
                }
                label="Patient"
              />
              <CheckInput
                checked={
                  formValues.consentInformation.informedConsentObtainedBy
                    .patientAndCaregiver
                }
                label="Patient & Alternate Caregiver"
              />
              <CheckInput
                checked={
                  formValues.consentInformation.informedConsentObtainedBy
                    .pharmacist
                }
                label="Pharmacist Acknowledgement/Consent"
              />
            </Flex>
          </Flex>
        </PageContent>
      </Page>

      <Page
        size="A4"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 30,
        }}
      >
        <PageHeading pharmacyName="Pharmacy Name/Logo" />
        <PageContent>
          <Flex gap={16}>
            <H2 filled>Pre-Assessment - Special Populations</H2>
            <Flex gap={8}>
              <FlexRow gap={10} style={{ width: "100%" }}>
                <CheckInput
                  checked={
                    formValues.preAssessmentSpecialPopulations.pregnancy.checked
                  }
                  label="Pregnant - Trimester:"
                />
                <View
                  style={{
                    borderBottom: "1px solid #D0D0D0",
                    minWidth: 133,
                    maxWidth: 133,
                  }}
                >
                  <P>
                    {
                      formValues.preAssessmentSpecialPopulations.pregnancy
                        .trimester
                    }
                  </P>
                </View>
                <P>Expectancy Date:</P>
                <View
                  style={{
                    borderBottom: "1px solid #D0D0D0",
                    flexGrow: 1,
                  }}
                >
                  <P>
                    {
                      formValues.preAssessmentSpecialPopulations.pregnancy
                        .expectancyDate
                    }
                  </P>
                </View>
              </FlexRow>
              <FlexRow gap={10} style={{ width: "100%" }}>
                <CheckInput
                  checked={
                    formValues.preAssessmentSpecialPopulations.breastfeeding
                      .checked
                  }
                  label="Breastfeeding - Age of Infant:"
                />
                <View
                  style={{
                    borderBottom: "1px solid #D0D0D0",
                    minWidth: 100,
                  }}
                >
                  <P>
                    {
                      formValues.preAssessmentSpecialPopulations.breastfeeding
                        .ageOfInfant
                    }
                  </P>
                </View>
              </FlexRow>
              <CheckInput
                checked={
                  formValues.preAssessmentSpecialPopulations
                    .residingInLongTermCare
                }
                label="Residing in Long Term Care"
              />
              <TextAreaInput
                label="Comments"
                lines={2}
                value={formValues.preAssessmentSpecialPopulations.comments}
              />
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H2 filled>Allergy Information</H2>
            <Flex gap={8}>
              <CheckInput
                checked={formValues.allergyInformation.noKnownAllergies}
                label="No Known Allergies"
              />
              <Flex gap={4}>
                <CheckInput
                  checked={formValues.allergyInformation.hasKnownAllergies}
                  label="Has Known Allergies"
                />
                <View style={{ paddingLeft: 22 }}>
                  <TextInput value={formValues.allergyInformation.allergies} />
                </View>
              </Flex>
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H2 filled>Lifestyle Factors</H2>
            <Flex gap={12}>
              <FlexRow>
                <View style={{ width: 120 }}>
                  <H3>Factor</H3>
                </View>
                <H3>Results</H3>
              </FlexRow>
              <Flex gap={8}>
                <FlexRow>
                  <View style={{ width: 120, minWidth: 120 }}>
                    <P>Alcohol intake</P>
                  </View>
                  <View
                    style={{
                      borderBottom: 1,
                      borderBottomColor: "#D0D0D0",
                      width: "100%",
                    }}
                  >
                    <P>{formValues.lifestyleFactors.alcohol}</P>
                  </View>
                </FlexRow>
                <FlexRow>
                  <View style={{ width: 120, minWidth: 120 }}>
                    <P>Caffeine intake</P>
                  </View>
                  <View
                    style={{
                      borderBottom: 1,
                      borderBottomColor: "#D0D0D0",
                      width: "100%",
                    }}
                  >
                    <P>{formValues.lifestyleFactors.caffeine}</P>
                  </View>
                </FlexRow>
                <FlexRow>
                  <View style={{ width: 120, minWidth: 120 }}>
                    <P>Smoking status</P>
                  </View>
                  <View
                    style={{
                      borderBottom: 1,
                      borderBottomColor: "#D0D0D0",
                      width: "100%",
                    }}
                  >
                    <P>{formValues.lifestyleFactors.smoking}</P>
                  </View>
                </FlexRow>
                <View style={{ paddingLeft: 22 }}>
                  <CheckInput
                    checked={
                      formValues.lifestyleFactors.smokingCessationConsent
                    }
                    label="Patient Consent for Enrolment in Pharmacy Directed Smoking Cessation Program"
                  />
                </View>
                <FlexRow>
                  <View style={{ width: 120, minWidth: 120 }}>
                    <P>Recreational Drugs</P>
                  </View>
                  <View
                    style={{
                      borderBottom: 1,
                      borderBottomColor: "#D0D0D0",
                      width: "100%",
                    }}
                  >
                    <P>{formValues.lifestyleFactors.recreationalDrugs}</P>
                  </View>
                </FlexRow>
                <FlexRow>
                  <View style={{ width: 120, minWidth: 120 }}>
                    <P>Exercise</P>
                  </View>
                  <View
                    style={{
                      borderBottom: 1,
                      borderBottomColor: "#D0D0D0",
                      width: "100%",
                    }}
                  >
                    <P>{formValues.lifestyleFactors.exercise}</P>
                  </View>
                </FlexRow>
                <FlexRow>
                  <View style={{ width: 120, minWidth: 120 }}>
                    <P>Cultural Factors</P>
                  </View>
                  <View
                    style={{
                      borderBottom: 1,
                      borderBottomColor: "#D0D0D0",
                      width: "100%",
                    }}
                  >
                    <P>{formValues.lifestyleFactors.culturalFactors}</P>
                  </View>
                </FlexRow>
              </Flex>
              <Flex gap={8}>
                <H3>Lifestyle Comments:</H3>
                <TextAreaInput
                  lines={2}
                  value={formValues.lifestyleFactors.lifestyleComments}
                />
              </Flex>
            </Flex>
          </Flex>
        </PageContent>
      </Page>

      <Page
        size="A4"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 30,
        }}
      >
        <PageHeading pharmacyName="Pharmacy Name/Logo" />
        <PageContent>
          <Flex gap={16}>
            <H2 filled>Reason(s) for Referral</H2>
            <CheckInput
              checked={
                formValues.reasonsForReferral
                  .primaryPhysicianOrAltHealthcareProvider
              }
              label="Referral to Primary Provider or Alternate Healthcare Provider"
            />
          </Flex>
          <Flex gap={16}>
            <H3>Presenting Symptoms & Minor Ailment</H3>
            <Flex gap={8}>
              <CheckInput
                checked={
                  formValues.reasonsForReferral
                    .presentingSymptomsAndMinorAilment
                    .noSymptomsMatchMinorAilments
                }
                label="No symptoms match minor ailments"
              />
              <CheckInput
                checked={
                  formValues.reasonsForReferral
                    .presentingSymptomsAndMinorAilment.noTypicalSignsAndSymptoms
                }
                label="No Typical Signs & Symptoms"
              />
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H3>Special Population</H3>
            <Flex gap={8}>
              <CheckInput
                checked={
                  formValues.reasonsForReferral.specialPopulation.pregnant
                }
                label="Pregnant"
              />
              <CheckInput
                checked={
                  formValues.reasonsForReferral.specialPopulation.breastfeeding
                }
                label="Breastfeeding"
              />
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H3>Red Flag Signs & Symptoms</H3>
            <Flex gap={8}>
              <Flex gap={4}>
                <CheckInput
                  checked={
                    formValues.reasonsForReferral.redFlagSignsAndSymptoms
                      .redFlagSymptoms
                  }
                  label="Red Flag Symptoms"
                />
                <View style={{ paddingLeft: 22 }}>
                  <TextInput
                    value={
                      formValues.reasonsForReferral.redFlagSignsAndSymptoms
                        .redFlagSymptomsValue
                    }
                  />
                </View>
              </Flex>
              <CheckInput
                checked={
                  formValues.reasonsForReferral.redFlagSignsAndSymptoms
                    .infectiveRhinitis
                }
                label="Infective Rhinitis"
              />
              <CheckInput
                checked={
                  formValues.reasonsForReferral.redFlagSignsAndSymptoms
                    .chronicRhinosinusitis
                }
                label="Chronic Rhinosinusitis"
              />
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H3>Red Flag Medical Conditions</H3>
            <Flex gap={8}>
              <CheckInput
                checked={
                  formValues.reasonsForReferral.redFlagMedicalConditions
                    .otitisMedia
                }
                label="Otitis Media"
              />
              <CheckInput
                checked={
                  formValues.reasonsForReferral.redFlagMedicalConditions
                    .chronicRecurrentSinusitis
                }
                label="Chronic, Recurrent Sinusitis"
              />
              <CheckInput
                checked={
                  formValues.reasonsForReferral.redFlagMedicalConditions
                    .uncontrolledThyroid
                }
                label="Uncontrolled Thyroid"
              />
              <CheckInput
                checked={
                  formValues.reasonsForReferral.redFlagMedicalConditions
                    .uncontrolledAsthma
                }
                label="Uncontrolled Asthma"
              />
            </Flex>
          </Flex>
        </PageContent>
      </Page>

      <Page
        size="A4"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 30,
        }}
      >
        <PageHeading pharmacyName="Pharmacy Name/Logo" />
        <PageContent>
          <Flex gap={16}>
            <H3>Drug Induced Allergic Rhinitis</H3>
            <Flex gap={12}>
              <H4>
                Recently started a new medication and coinciding Allergic Rhinitis symptoms:
              </H4>
              <Flex gap={8}>
                <Flex gap={4}>
                  <CheckInput
                    checked={
                      formValues.reasonsForReferral.drugInducedAllergicRhinitis
                        .prescriptionMedications
                    }
                    label="Prescription Medications"
                  />
                  <View style={{ paddingLeft: 22 }}>
                    <TextInput
                      value={
                        formValues.reasonsForReferral
                          .drugInducedAllergicRhinitis
                          .prescriptionMedicationsValue
                      }
                    />
                  </View>
                </Flex>
                <CheckInput
                  checked={
                    formValues.reasonsForReferral.drugInducedAllergicRhinitis
                      .oralContraceptives
                  }
                  label="Oral Contraceptives"
                />
                <CheckInput
                  checked={
                    formValues.reasonsForReferral.drugInducedAllergicRhinitis
                      .nonPrescriptionMedications
                  }
                  label="Non Prescription Medications"
                />
                <CheckInput
                  checked={
                    formValues.reasonsForReferral.drugInducedAllergicRhinitis
                      .herbalVitamins
                  }
                  label="Herbal / Vitamins"
                />
                <CheckInput
                  checked={
                    formValues.reasonsForReferral.drugInducedAllergicRhinitis
                      .sampleMedications
                  }
                  label="Sample Medications"
                />
                <CheckInput
                  checked={
                    formValues.reasonsForReferral.drugInducedAllergicRhinitis
                      .cocaine
                  }
                  label="Cocaine"
                />
              </Flex>
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H3>Differential Assessment</H3>
            <Flex gap={8}>
              <Flex gap={4}>
                <Flex gap={0}>
                  <CheckInput
                    checked={
                      formValues.reasonsForReferral.differentialAssessment
                        .irritantRhinitis
                    }
                    label="Irritant (Non-Allergic) Rhinitis - exposure to a physical or chemical irritant"
                  />
                  <View style={{ paddingLeft: 22 }}>
                    <P>Name if Irritant if known:</P>
                  </View>
                </Flex>
                <View style={{ paddingLeft: 22 }}>
                  <TextInput
                    value={
                      formValues.reasonsForReferral.differentialAssessment
                        .irritantRhinitisValue
                    }
                  />
                </View>
              </Flex>
              <CheckInput
                checked={
                  formValues.reasonsForReferral.differentialAssessment
                    .occupationalRhinitis
                }
                label="Occupational Rhinitis"
              />
              <CheckInput
                checked={
                  formValues.reasonsForReferral.differentialAssessment
                    .structuralOrMechanical
                }
                label="Structural or Mechanical Factors"
              />
              <CheckInput
                checked={
                  formValues.reasonsForReferral.differentialAssessment
                    .systemicConditions
                }
                label="Systemic Conditions"
              />
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H3>Interventions</H3>
            <Flex gap={8}>
              <CheckInput
                checked={
                  formValues.reasonsForReferral.interventions
                    .suggestedToDiscontinueMedication
                }
                label="Suggested to discontinue medication"
              />
              <CheckInput
                checked={
                  formValues.reasonsForReferral.interventions
                    .suggestedToChangeMedication
                }
                label="Suggested to change medication"
              />
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H2 filled>Typical Signs and Symptoms</H2>
            <Flex gap={8}>
              <Flex gap={6}>
                <CheckInput
                  checked={
                    formValues.typicalSignsAndSymptoms.nasalSymptoms.checked
                  }
                  label="Nasal Symptoms"
                />
                <View style={{ paddingLeft: 22 }}>
                  <FlexRow gap={10}>
                    <CheckInput
                      checked={
                        formValues.typicalSignsAndSymptoms.nasalSymptoms
                          .rhinorrhea
                      }
                      label="Rhinorrhea"
                      minWidth={157}
                    />
                    <CheckInput
                      checked={
                        formValues.typicalSignsAndSymptoms.nasalSymptoms
                          .itchyNose
                      }
                      label="Itchy nose (eyes, or throat)"
                    />
                    <CheckInput
                      checked={
                        formValues.typicalSignsAndSymptoms.nasalSymptoms
                          .sneezing
                      }
                      label="Sneezing"
                    />
                  </FlexRow>
                </View>
              </Flex>
              <CheckInput
                checked={formValues.typicalSignsAndSymptoms.otherARSymptoms}
                label="Other Allergic Rhinitis Symptoms"
              />
              <CheckInput
                checked={formValues.typicalSignsAndSymptoms.nasalCongestion}
                label="Nasal Congestion: Early Phase/ Late Phase / None"
              />
              <CheckInput
                checked={formValues.typicalSignsAndSymptoms.eyeSymptoms}
                label="Eye Symptoms"
              />
            </Flex>
          </Flex>
        </PageContent>
      </Page>

      <Page
        size="A4"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 30,
        }}
      >
        <PageHeading pharmacyName="Pharmacy Name/Logo" />
        <PageContent>
          <Flex gap={16}>
            <H3>Clinical (Visible) Signs & Symptoms</H3>
            <Flex gap={8}>
              <CheckInput
                checked={
                  formValues.typicalSignsAndSymptoms.clinical.allergicSalute
                }
                label="Allergic salute is a sign more commonly seen in children (hand frequently wipes the nose in an upward motion)."
              />
              <CheckInput
                checked={
                  formValues.typicalSignsAndSymptoms.clinical.allergicShiners
                }
                label="Allergic shiners (dark circles under the eyes that are due to nasal congestion)"
              />
              <Flex gap={0}>
                <CheckInput
                  checked={
                    formValues.typicalSignsAndSymptoms.clinical
                      .noTypicalSignsAndSymptoms
                  }
                  label="No Typical Signs & Symptoms present"
                />
                <View style={{ paddingLeft: 22 }}>
                  <P>
                    Referral to Primary Provider or Alternate Healthcare
                    Provider
                  </P>
                </View>
              </Flex>
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H2 filled>Classification of Minor Ailment - Allergic Rhinitis</H2>
            <Flex gap={12}>
              <H4>Symptom Duration/ Frequency/ Level of Severity:</H4>
              <Flex gap={8}>
                <CheckInput
                  checked={
                    formValues.classificationOfMinorAilment.symptomDuration
                      .mildIntermittent
                  }
                  label={
                    <>
                      <P style={{ fontWeight: 600 }} span>
                        Mild Intermittent: Symptoms less than 4 days per week OR
                        less than 4 consecutive weeks.
                      </P>
                      {"\n"}
                      Symptoms DO NOT interfere with daily activities (work,
                      school, sport, leisure). Symptoms are not bothersome and
                      have LITTLE to NO effect on sleep.
                    </>
                  }
                />
                <CheckInput
                  checked={
                    formValues.classificationOfMinorAilment.symptomDuration
                      .mildPersistent
                  }
                  label={
                    <>
                      <P style={{ fontWeight: 600 }} span>
                        Mild Persistent: Symptoms on 4 or more days per week AND
                        more than 4 consecutive weeks.
                      </P>{" "}
                      Symptoms DO NOT interfere with daily activities (work,
                      school, sport, leisure). Symptoms are not bothersome and
                      have LITTLE to NO effect on sleep.
                    </>
                  }
                />
                <CheckInput
                  checked={
                    formValues.classificationOfMinorAilment.symptomDuration
                      .moderateSevereIntermittent
                  }
                  label={
                    <>
                      <P style={{ fontWeight: 600 }} span>
                        Moderate to Severe Intermittent: Symptoms less than 4
                        days per week OR less than 4{"\n"}consecutive weeks.
                      </P>{" "}
                      Symptoms interfere significantly in performance of normal
                      daily activities.{"\n"}Symptoms are bothersome and DISTURB
                      sleep.
                    </>
                  }
                />
                <CheckInput
                  checked={
                    formValues.classificationOfMinorAilment.symptomDuration
                      .moderateSeverePersistent
                  }
                  label={
                    <>
                      <P style={{ fontWeight: 600 }} span>
                        Moderate to Severe Persistent: Symptoms on 4 or more
                        days per week AND more than 4{"\n"}consecutive weeks.
                      </P>{" "}
                      Symptoms interfere significantly in performance of normal
                      daily activities.{"\n"}Symptoms are bothersome and DISTURB
                      sleep.
                    </>
                  }
                />
              </Flex>
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H3>Symptoms correlate with exposure to an allergen</H3>
            <FlexRow gap={10}>
              <View style={{ minWidth: 152 }}>
                <CheckInput
                  checked={
                    formValues.classificationOfMinorAilment
                      .symptomsCorrelateAllergenExposure
                  }
                  label="Yes"
                />
              </View>
              <CheckInput
                checked={
                  !formValues.classificationOfMinorAilment
                    .symptomsCorrelateAllergenExposure
                }
                label="No"
              />
            </FlexRow>
          </Flex>
          <Flex gap={16}>
            <H2 filled>Treatment Information</H2>
            <Flex gap={12}>
              <H3>Goals for Therapy - Allergic Rhinitis</H3>
              <Flex gap={8}>
                <ListItem>
                  Provide Relief from signs and symptoms from allergies
                </ListItem>
                <ListItem>
                  Prevent symptoms by avoiding allergen exposure
                </ListItem>
                <ListItem>Minimize adverse effects of treatment</ListItem>
                <ListItem>Improve quality of life</ListItem>
              </Flex>
            </Flex>
          </Flex>
        </PageContent>
      </Page>

      <Page
        size="A4"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 30,
        }}
      >
        <PageHeading pharmacyName="Pharmacy Name/Logo" />
        <PageContent>
          <Flex gap={16}>
            <H2 filled>Treatment History (if applicable)</H2>
            <Flex gap={12}>
              <H3>Pharmacologic or Non-Pharmacologic Treatment</H3>
              <CheckInput
                checked={formValues.treatmentHistory.noPastMedications}
                label="No past medications"
              />
              <Flex gap={8}>
                <CheckInput
                  checked={
                    formValues.treatmentHistory.allergenAvoidance.checked
                  }
                  label={
                    <P span style={{ fontWeight: 600 }}>
                      Non-Pharm: Allergen/ Irritant Avoidance
                    </P>
                  }
                />
                <View
                  style={{
                    paddingLeft: 22,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <FlexRow gap={10}>
                    <P>Comments:</P>
                    <View
                      style={{
                        borderBottom: "1px solid #D0D0D0",
                        flexGrow: 1,
                      }}
                    >
                      <P>
                        {formValues.treatmentHistory.allergenAvoidance.comments}
                      </P>
                    </View>
                  </FlexRow>
                  <FlexRow gap={10}>
                    <P>How long did you avoid the allergens?</P>
                    <View
                      style={{
                        borderBottom: "1px solid #D0D0D0",
                        flexGrow: 1,
                      }}
                    >
                      <P>
                        {formValues.treatmentHistory.allergenAvoidance.howLong}
                      </P>
                    </View>
                  </FlexRow>
                  <FlexRow gap={10}>
                    <P style={{ width: 160 }}>Was is effective?</P>
                    <CheckInput
                      checked={
                        formValues.treatmentHistory.allergenAvoidance.effective
                      }
                      label="Yes"
                      minWidth={160}
                    />
                    <CheckInput
                      checked={
                        !formValues.treatmentHistory.allergenAvoidance.effective
                      }
                      label="No"
                    />
                  </FlexRow>
                </View>
              </Flex>
              <Flex gap={8}>
                <CheckInput
                  checked={
                    formValues.treatmentHistory.nasalIrrigationLubricant.checked
                  }
                  label={
                    <P span style={{ fontWeight: 600 }}>
                      Non-Pharm: Nasal Irrigation/ Nasal Lubricant(s)
                    </P>
                  }
                />
                <View
                  style={{
                    paddingLeft: 22,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <FlexRow gap={10}>
                    <P>Comments:</P>
                    <View
                      style={{
                        borderBottom: "1px solid #D0D0D0",
                        flexGrow: 1,
                      }}
                    >
                      <P>
                        {
                          formValues.treatmentHistory.nasalIrrigationLubricant
                            .comments
                        }
                      </P>
                    </View>
                  </FlexRow>
                  <FlexRow gap={10}>
                    <P>How long did you use the medication(s)?</P>
                    <View
                      style={{
                        borderBottom: "1px solid #D0D0D0",
                        flexGrow: 1,
                      }}
                    >
                      <P>
                        {
                          formValues.treatmentHistory.nasalIrrigationLubricant
                            .howLong
                        }
                      </P>
                    </View>
                  </FlexRow>
                  <FlexRow gap={10}>
                    <P style={{ width: 160 }}>Was is effective?</P>
                    <CheckInput
                      checked={
                        formValues.treatmentHistory.nasalIrrigationLubricant
                          .effective
                      }
                      label="Yes"
                      minWidth={160}
                    />
                    <CheckInput
                      checked={
                        !formValues.treatmentHistory.nasalIrrigationLubricant
                          .effective
                      }
                      label="No"
                    />
                  </FlexRow>
                  <FlexRow gap={10}>
                    <P style={{ width: 160 }}>Any side effects?</P>
                    <CheckInput
                      checked={
                        formValues.treatmentHistory.nasalIrrigationLubricant
                          .sideEffects
                      }
                      label="Yes"
                      minWidth={160}
                    />
                    <CheckInput
                      checked={
                        !formValues.treatmentHistory.nasalIrrigationLubricant
                          .sideEffects
                      }
                      label="No"
                    />
                  </FlexRow>
                  <FlexRow gap={10}>
                    <P>Comments:</P>
                    <View
                      style={{
                        borderBottom: "1px solid #D0D0D0",
                        flexGrow: 1,
                      }}
                    >
                      <P>
                        {
                          formValues.treatmentHistory.nasalIrrigationLubricant
                            .sideEffectsComments
                        }
                      </P>
                    </View>
                  </FlexRow>
                </View>
              </Flex>
              <Flex gap={8}>
                <CheckInput
                  checked={
                    formValues.treatmentHistory.oralNasalDecongestant.checked
                  }
                  label={
                    <P span style={{ fontWeight: 600 }}>
                      OTC: Oral and/ or Nasal Decongestant(s)
                    </P>
                  }
                />
                <View
                  style={{
                    paddingLeft: 22,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <FlexRow gap={10}>
                    <P>Comments:</P>
                    <View
                      style={{
                        borderBottom: "1px solid #D0D0D0",
                        flexGrow: 1,
                      }}
                    >
                      <P>
                        {
                          formValues.treatmentHistory.oralNasalDecongestant
                            .comments
                        }
                      </P>
                    </View>
                  </FlexRow>
                  <FlexRow gap={10}>
                    <P>How long did you use the medication(s)?</P>
                    <View
                      style={{
                        borderBottom: "1px solid #D0D0D0",
                        flexGrow: 1,
                      }}
                    >
                      <P>
                        {
                          formValues.treatmentHistory.oralNasalDecongestant
                            .howLong
                        }
                      </P>
                    </View>
                  </FlexRow>
                  <FlexRow gap={10}>
                    <P style={{ width: 160 }}>Was is effective?</P>
                    <CheckInput
                      checked={
                        formValues.treatmentHistory.oralNasalDecongestant
                          .effective
                      }
                      label="Yes"
                      minWidth={160}
                    />
                    <CheckInput
                      checked={
                        !formValues.treatmentHistory.oralNasalDecongestant
                          .effective
                      }
                      label="No"
                    />
                  </FlexRow>
                  <FlexRow gap={10}>
                    <P style={{ width: 160 }}>Any side effects?</P>
                    <CheckInput
                      checked={
                        formValues.treatmentHistory.oralNasalDecongestant
                          .sideEffects
                      }
                      label="Yes"
                      minWidth={160}
                    />
                    <CheckInput
                      checked={
                        !formValues.treatmentHistory.oralNasalDecongestant
                          .sideEffects
                      }
                      label="No"
                    />
                  </FlexRow>
                  <FlexRow gap={10}>
                    <P>Comments:</P>
                    <View
                      style={{
                        borderBottom: "1px solid #D0D0D0",
                        flexGrow: 1,
                      }}
                    >
                      <P>
                        {
                          formValues.treatmentHistory.oralNasalDecongestant
                            .sideEffectsComments
                        }
                      </P>
                    </View>
                  </FlexRow>
                </View>
              </Flex>
              <Flex gap={8}>
                <CheckInput
                  checked={
                    formValues.treatmentHistory.oralAntihistamine.checked
                  }
                  label={
                    <P span style={{ fontWeight: 600 }}>
                      OTC or Rx: Oral antihistamine(s)
                    </P>
                  }
                />
                <View
                  style={{
                    paddingLeft: 22,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <FlexRow gap={10}>
                    <P>Comments:</P>
                    <View
                      style={{
                        borderBottom: "1px solid #D0D0D0",
                        flexGrow: 1,
                      }}
                    >
                      <P>
                        {formValues.treatmentHistory.oralAntihistamine.comments}
                      </P>
                    </View>
                  </FlexRow>
                  <FlexRow gap={10}>
                    <P>How long did you use the medication(s)?</P>
                    <View
                      style={{
                        borderBottom: "1px solid #D0D0D0",
                        flexGrow: 1,
                      }}
                    >
                      <P>
                        {formValues.treatmentHistory.oralAntihistamine.howLong}
                      </P>
                    </View>
                  </FlexRow>
                  <FlexRow gap={10}>
                    <P style={{ width: 160 }}>Was is effective?</P>
                    <CheckInput
                      checked={
                        formValues.treatmentHistory.oralAntihistamine.effective
                      }
                      label="Yes"
                      minWidth={160}
                    />
                    <CheckInput
                      checked={
                        !formValues.treatmentHistory.oralAntihistamine.effective
                      }
                      label="No"
                    />
                  </FlexRow>
                  <FlexRow gap={10}>
                    <P style={{ width: 160 }}>Any side effects?</P>
                    <CheckInput
                      checked={
                        formValues.treatmentHistory.oralAntihistamine
                          .sideEffects
                      }
                      label="Yes"
                      minWidth={160}
                    />
                    <CheckInput
                      checked={
                        !formValues.treatmentHistory.oralAntihistamine
                          .sideEffects
                      }
                      label="No"
                    />
                  </FlexRow>
                  <FlexRow gap={10}>
                    <P>Comments:</P>
                    <View
                      style={{
                        borderBottom: "1px solid #D0D0D0",
                        flexGrow: 1,
                      }}
                    >
                      <P>
                        {
                          formValues.treatmentHistory.oralAntihistamine
                            .sideEffectsComments
                        }
                      </P>
                    </View>
                  </FlexRow>
                </View>
              </Flex>
              <Flex gap={8}>
                <CheckInput
                  checked={
                    formValues.treatmentHistory.intranasalCorticosteroid.checked
                  }
                  label={
                    <P span style={{ fontWeight: 600 }}>
                      OTC or Rx: Intranasal Corticosteroid(s)
                    </P>
                  }
                />
                <View
                  style={{
                    paddingLeft: 22,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <FlexRow gap={10}>
                    <P>Comments:</P>
                    <View
                      style={{
                        borderBottom: "1px solid #D0D0D0",
                        flexGrow: 1,
                      }}
                    >
                      <P>
                        {
                          formValues.treatmentHistory.intranasalCorticosteroid
                            .comments
                        }
                      </P>
                    </View>
                  </FlexRow>
                  <FlexRow gap={10}>
                    <P>How long did you use the medication(s)?</P>
                    <View
                      style={{
                        borderBottom: "1px solid #D0D0D0",
                        flexGrow: 1,
                      }}
                    >
                      <P>
                        {
                          formValues.treatmentHistory.intranasalCorticosteroid
                            .howLong
                        }
                      </P>
                    </View>
                  </FlexRow>
                  <FlexRow gap={10}>
                    <P style={{ width: 160 }}>Was is effective?</P>
                    <CheckInput
                      checked={
                        formValues.treatmentHistory.intranasalCorticosteroid
                          .effective
                      }
                      label="Yes"
                      minWidth={160}
                    />
                    <CheckInput
                      checked={
                        !formValues.treatmentHistory.intranasalCorticosteroid
                          .effective
                      }
                      label="No"
                    />
                  </FlexRow>
                  <FlexRow gap={10}>
                    <P style={{ width: 160 }}>Any side effects?</P>
                    <CheckInput
                      checked={
                        formValues.treatmentHistory.intranasalCorticosteroid
                          .sideEffects
                      }
                      label="Yes"
                      minWidth={160}
                    />
                    <CheckInput
                      checked={
                        !formValues.treatmentHistory.intranasalCorticosteroid
                          .sideEffects
                      }
                      label="No"
                    />
                  </FlexRow>
                  <FlexRow gap={10}>
                    <P>Comments:</P>
                    <View
                      style={{
                        borderBottom: "1px solid #D0D0D0",
                        flexGrow: 1,
                      }}
                    >
                      <P>
                        {
                          formValues.treatmentHistory.intranasalCorticosteroid
                            .sideEffectsComments
                        }
                      </P>
                    </View>
                  </FlexRow>
                </View>
              </Flex>
            </Flex>
          </Flex>
        </PageContent>
      </Page>

      <Page
        size="A4"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 30,
        }}
      >
        <PageHeading pharmacyName="Pharmacy Name/Logo" />
        <PageContent>
          <Flex gap={8}>
            <CheckInput
              checked={
                formValues.treatmentHistory
                  .intranasalAntihistamineCorticosteroid.checked
              }
              label={
                <P span style={{ fontWeight: 600 }}>
                  Rx: Intranasal antihistamine/corticosteroid
                </P>
              }
            />
            <View
              style={{
                paddingLeft: 22,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <FlexRow gap={10}>
                <P>Comments:</P>
                <View
                  style={{
                    borderBottom: "1px solid #D0D0D0",
                    flexGrow: 1,
                  }}
                >
                  <P>
                    {
                      formValues.treatmentHistory
                        .intranasalAntihistamineCorticosteroid.comments
                    }
                  </P>
                </View>
              </FlexRow>
              <FlexRow gap={10}>
                <P>How long did you use the medication(s)?</P>
                <View
                  style={{
                    borderBottom: "1px solid #D0D0D0",
                    flexGrow: 1,
                  }}
                >
                  <P>
                    {
                      formValues.treatmentHistory
                        .intranasalAntihistamineCorticosteroid.howLong
                    }
                  </P>
                </View>
              </FlexRow>
              <FlexRow gap={10}>
                <P style={{ width: 160 }}>Was is effective?</P>
                <CheckInput
                  checked={
                    formValues.treatmentHistory
                      .intranasalAntihistamineCorticosteroid.effective
                  }
                  label="Yes"
                  minWidth={160}
                />
                <CheckInput
                  checked={
                    !formValues.treatmentHistory
                      .intranasalAntihistamineCorticosteroid.effective
                  }
                  label="No"
                />
              </FlexRow>
              <FlexRow gap={10}>
                <P style={{ width: 160 }}>Any side effects?</P>
                <CheckInput
                  checked={
                    formValues.treatmentHistory
                      .intranasalAntihistamineCorticosteroid.sideEffects
                  }
                  label="Yes"
                  minWidth={160}
                />
                <CheckInput
                  checked={
                    !formValues.treatmentHistory
                      .intranasalAntihistamineCorticosteroid.sideEffects
                  }
                  label="No"
                />
              </FlexRow>
              <FlexRow gap={10}>
                <P>Comments:</P>
                <View
                  style={{
                    borderBottom: "1px solid #D0D0D0",
                    flexGrow: 1,
                  }}
                >
                  <P>
                    {
                      formValues.treatmentHistory
                        .intranasalAntihistamineCorticosteroid
                        .sideEffectsComments
                    }
                  </P>
                </View>
              </FlexRow>
            </View>
          </Flex>
          <Flex gap={16}>
            <H2 filled>Treatment for “Minor Ailment Name” </H2>
            <CheckInput
              checked={formValues.treatment.prescriptionsIssued}
              label="Prescriptions Issued (see Rx attached)"
            />
            <Flex gap={12}>
              <Flex gap={8}>
                <H3>Prevention Treatment</H3>
                <P>Added to Storyboard / Education take away component</P>
              </Flex>
              <Flex gap={8}>
                <H3>Non Pharmacological Treatment</H3>
                <CheckInput
                  checked={
                    formValues.treatment.nonPharmacologicalTreatment
                      .allergenAvoidance
                  }
                  label="Allergen Avoidance Tips / Prevention"
                />
                <CheckInput
                  checked={
                    formValues.treatment.nonPharmacologicalTreatment.nasalRinses
                  }
                  label="Nasal RInses / Sprays/ Drops"
                />
                <CheckInput
                  checked={
                    formValues.treatment.nonPharmacologicalTreatment
                      .nasalLubricants
                  }
                  label="Nasal lubricants"
                />
              </Flex>
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H3>Pharmacological Treatment</H3>
            <Flex gap={12}>
              <H4>Mild Intermittent</H4>
              <Flex gap={8}>
                <CheckInput
                  checked={
                    formValues.treatment.pharmacologicalTreatment
                      .mildIntermittent.oralAntihistamine
                  }
                  label="Oral antihistamine (second generation)"
                />
                <View style={{ paddingLeft: 22 }}>
                  <TextInput
                    value={
                      formValues.treatment.pharmacologicalTreatment
                        .mildIntermittent.oralAntihistamineName
                    }
                  />
                </View>
                <CheckInput
                  checked={
                    formValues.treatment.pharmacologicalTreatment
                      .mildIntermittent.intranasalCorticosteroid
                  }
                  label="Intranasal corticosteroid if above agent(s) are ineffective or not tolerated"
                />
                <CheckInput
                  checked={
                    formValues.treatment.pharmacologicalTreatment
                      .mildIntermittent.intranasalCorticosteroidAntihistamine
                  }
                  label="Intranasal corticosteroid/intranasal antihistamine if above agents are ineffective/not tolerated"
                />
              </Flex>
            </Flex>
            <Flex gap={12}>
              <H4>Mild Persistent</H4>
              <Flex gap={8}>
                <CheckInput
                  checked={
                    formValues.treatment.pharmacologicalTreatment.mildPersistent
                      .intranasalCorticosteroid
                  }
                  label="Intranasal corticosteroid"
                />
                <CheckInput
                  checked={
                    formValues.treatment.pharmacologicalTreatment.mildPersistent
                      .intranasalCorticosteroidAntihistamine
                  }
                  label="Intranasal corticosteroid/intranasal antihistamine if above agents are ineffective/not tolerated"
                />
              </Flex>
            </Flex>
            <Flex gap={12}>
              <H4>Moderate to Severe - Intermittent or Persistent</H4>
              <Flex gap={8}>
                <CheckInput
                  checked={
                    formValues.treatment.pharmacologicalTreatment
                      .moderateToSevere.intranasalCorticosteroid
                  }
                  label="Intranasal corticosteroid"
                />
                <CheckInput
                  checked={
                    formValues.treatment.pharmacologicalTreatment
                      .moderateToSevere.intranasalCorticosteroidAntihistamine
                  }
                  label="Intranasal corticosteroid/intranasal antihistamine if above agents are ineffective"
                />
              </Flex>
            </Flex>
          </Flex>
        </PageContent>
      </Page>

      <Page
        size="A4"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 30,
        }}
      >
        <PageHeading pharmacyName="Pharmacy Name/Logo" />
        <PageContent>
          <Flex gap={12}>
            <H3>Add-on Therapy</H3>
            <Flex gap={8}>
              <CheckInput
                checked={formValues.treatment.addOnTherapy.oralDecongestant}
                label="Oral decongestant"
              />
              <CheckInput
                checked={formValues.treatment.addOnTherapy.oralAntihistamines}
                label="Oral antihistamines"
              />
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H2 filled>OTC Consult Information (If Applicable)</H2>
            <Flex gap={12}>
              <Flex gap={8}>
                <CheckInput
                  checked={formValues.otcConsultInformation.otcConsultProvided}
                  label="OTC Consult Provided"
                />
                <CheckInput
                  checked={
                    formValues.otcConsultInformation.otcRecommendationsIssued
                  }
                  label="OTC Recommendations issued (See Rx attached)"
                />
              </Flex>
              <Flex gap={8}>
                <H3>Non-pharmacological Treatment</H3>
                <CheckInput
                  checked={
                    formValues.otcConsultInformation.nonPharmacologicalTreatment
                      .allergenAvoidanceTips
                  }
                  label="Allergen Avoidance Tips / Prevention"
                />
                <CheckInput
                  checked={
                    formValues.otcConsultInformation.nonPharmacologicalTreatment
                      .nasalRinses
                  }
                  label="Nasal Rinses / Sprays/ Drops"
                />
                <CheckInput
                  checked={
                    formValues.otcConsultInformation.nonPharmacologicalTreatment
                      .nasalLubricants
                  }
                  label="Nasal lubricants"
                />
              </Flex>
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H2 filled>Educational Information</H2>
            <Flex gap={8}>
              <CheckInput
                checked={formValues.educationalInformation.educationProvided}
                label="Education provided for the Prescriptions issued"
              />
              <CheckInput
                checked={formValues.educationalInformation.storyboardProvided}
                label={
                  <>
                    Storyboard provided to the patient as a take away for
                    additional education including instructions{"\n"}for
                    medications, assess for benefits, and manage side effects,
                    if applicable.
                  </>
                }
              />
            </Flex>
          </Flex>
          <Flex gap={12}>
            <H2 filled>Notification Type</H2>
            <Flex gap={8} style={{ maxWidth: 455 }}>
              <FlexRow gap={10}>
                <CheckInput
                  checked={
                    formValues.notificationType.primaryCarePhysician.checked
                  }
                  label="Primary Care Physician - Name:"
                />
                <View
                  style={{ borderBottom: "1px solid #D0D0D0", flexGrow: 1 }}
                >
                  <P>{formValues.notificationType.primaryCarePhysician.name}</P>
                </View>
              </FlexRow>
              <FlexRow gap={10}>
                <CheckInput
                  checked={
                    formValues.notificationType.altHealthCareProvider.checked
                  }
                  label="Alternate Health Care Provider - Designation:"
                />
                <View
                  style={{ borderBottom: "1px solid #D0D0D0", flexGrow: 1 }}
                >
                  <P>
                    {
                      formValues.notificationType.altHealthCareProvider
                        .designation
                    }
                  </P>
                </View>
              </FlexRow>
              <FlexRow gap={10} style={{ paddingLeft: 22 }}>
                <P>Name:</P>
                <View
                  style={{ borderBottom: "1px solid #D0D0D0", flexGrow: 1 }}
                >
                  <P>
                    {formValues.notificationType.altHealthCareProvider.name}
                  </P>
                </View>
              </FlexRow>
              <CheckInput
                checked={
                  !formValues.notificationType.primaryCarePhysician.checked &&
                  !formValues.notificationType.altHealthCareProvider.checked
                }
                label="No Primary Provider or Alternate Health Care Provider"
              />
            </Flex>
            <FlexRow gap={10}>
              <H3>Date of Notification</H3>
              <P style={{ flexGrow: 1 }}>{formValues.notificationType.date}</P>
            </FlexRow>
            <H3>Method of Notification</H3>
            <Flex gap={8}>
              <CheckInput
                checked={formValues.notificationType.method.email}
                label="Email"
              />
              <CheckInput
                checked={formValues.notificationType.method.fax}
                label="Fax"
              />
              <CheckInput
                checked={formValues.notificationType.method.phone}
                label="Phone"
              />
              <CheckInput
                checked={formValues.notificationType.method.inPerson}
                label="In-person"
              />
            </Flex>
          </Flex>
        </PageContent>
      </Page>

      <Page
        size="A4"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 30,
        }}
      >
        <PageHeading pharmacyName="Pharmacy Name/Logo" />
        <PageContent>
          <Flex gap={16}>
            <H2 filled>
              Follow-up Information [Minor Ailment Name]{"\n"}
              <Text span style={{ fontSize: 11, fontWeight: 600 }}>
                (Only required if treating)
              </Text>
            </H2>
            <FlexRow gap={10}>
              <H3>Follow-up Date</H3>
              <P style={{ flexGrow: 1 }}>
                {formValues.followUpInformation.date}
              </P>
            </FlexRow>
          </Flex>
          <Flex gap={16}>
            <H2 filled>Prescribing Pharmacist Information</H2>
            <Flex gap={12}>
              <TextInput
                label="Pharmacist Name"
                value={formValues.prescribingPharmacistInformation.name}
              />
              <TextInput
                label="Pharmacist License Number"
                value={
                  formValues.prescribingPharmacistInformation.licenseNumber
                }
              />
              <TextAreaInput
                label="Pharmacist Signature"
                value={formValues.prescribingPharmacistInformation.signature}
                lines={2}
              />
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H2 filled>Pharmacy Information</H2>
            <Flex gap={12}>
              <TextInput
                label="Pharmacy Name"
                value={formValues.pharmacyInformation.name}
              />
              <FlexRow gap={12}>
                <TextInput
                  label="Store Number"
                  value={formValues.pharmacyInformation.storeNumber}
                />
                <View style={{ minWidth: 255.5 }} />
              </FlexRow>
              <FlexRow gap={12}>
                <TextInput
                  label="Pharmacy Phone"
                  value={formValues.pharmacyInformation.phone}
                />
                <TextInput
                  label="Pharmacy Fax"
                  value={formValues.pharmacyInformation.fax}
                />
              </FlexRow>
              <TextAreaInput
                label="Pharmacist Signature"
                value={formValues.pharmacyInformation.signature}
                lines={2}
              />
              <TextInput
                label="Pharmacy Address"
                value={formValues.pharmacyInformation.address}
              />
            </Flex>
          </Flex>
        </PageContent>
      </Page>
    </Document>
  );
};
